import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { HiSortAscending, HiSortDescending } from "react-icons/hi";
import { FiSearch, FiX } from "react-icons/fi";
import { usePrivacy } from "../../contexts/PrivacyContext";


const GroupHeader = ({
    text,
    sortingCriteria,
    sortOrder,
    setSortOrder,
    sortCriteria,
    setSortCriteria,
    students = [],
    selectedStudent,
    onStudentSelect,
    onStudentClear,
}) => {
    const { maskName, maskEmail } = usePrivacy();
    const [searchQuery, setSearchQuery] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const searchRef = useRef(null);
    const suggestionsRef = useRef(null);

    // Filter students based on search query
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredStudents([]);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = students.filter(student =>
            student.name.toLowerCase().includes(query) ||
            student.email.toLowerCase().includes(query)
        );
        setFilteredStudents(filtered);
        setShowSuggestions(filtered.length > 0);
    }, [searchQuery, students]);

    // Handle click outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchRef.current &&
                !searchRef.current.contains(event.target) &&
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Update search query when selected student changes
    useEffect(() => {
        if (selectedStudent) {
            setSearchQuery(selectedStudent.name);
        } else {
            setSearchQuery("");
        }
    }, [selectedStudent]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        if (selectedStudent && value !== selectedStudent.name) {
            onStudentClear();
        }
    };

    const handleStudentClick = (student) => {
        onStudentSelect(student);
        setShowSuggestions(false);
    };

    const handleClear = () => {
        setSearchQuery("");
        onStudentClear();
        setShowSuggestions(false);
    };

    const handleInputFocus = () => {
        if (filteredStudents.length > 0) {
            setShowSuggestions(true);
        }
    };

    return (
        <GroupInfo>
            <Title>{text}</Title>
            
            <DropdownGroup>
                {/* Search */}
                <Field>
                    <FieldLabel htmlFor="student-search">Search</FieldLabel>
                    <SearchWrapper ref={searchRef}>
                        <SearchInput
                            id="student-search"
                            type="text"
                            placeholder="Search student name..."
                            value={searchQuery}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                        />
                        {selectedStudent ? (
                            <ClearButton onClick={handleClear} type="button" aria-label="Clear selection">
                                <FiX />
                            </ClearButton>
                        ) : (
                            <SearchIcon>
                                <FiSearch />
                            </SearchIcon>
                        )}
                        {showSuggestions && filteredStudents.length > 0 && (
                            <SuggestionsList ref={suggestionsRef}>
                                {filteredStudents.map((student) => (
                                    <SuggestionItem
                                        key={student.email}
                                        onClick={() => handleStudentClick(student)}
                                    >
                                        <StudentAvatar bg={student.color || "#9333ea"}>
                                            {maskName(student.name).charAt(0).toUpperCase()}
                                        </StudentAvatar>
                                        <StudentInfo>
                                            <StudentName>{maskName(student.name)}</StudentName>
                                            <StudentEmail>{maskEmail(student.email)}</StudentEmail>
                                        </StudentInfo>
                                    </SuggestionItem>
                                ))}
                            </SuggestionsList>
                        )}
                    </SearchWrapper>
                </Field>
                {/* Sort by */}
                <Field>
                    <FieldLabel htmlFor="sort-by">Sort by</FieldLabel>
                    <SelectWrap>
                        <Select
                            id="sort-by"
                            value={sortCriteria}
                            onChange={(e) => setSortCriteria(e.target.value)}
                        >
                            {Object.entries(sortingCriteria).map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                            ))}
                        </Select>
                    </SelectWrap>
                </Field>
                {sortCriteria !== "inf121_default" && (
                    <Field as="div">
                        <FieldLabel htmlFor="order">
                            {sortOrder === "asc" ? "Asc" : "Desc"}
                        </FieldLabel>
                        <IconToggle
                            type="button"
                            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                            aria-label="Toggle sort order"
                            title={sortOrder === "asc" ? "Ascending" : "Descending"}
                        >
                            {sortOrder === "asc" ? <HiSortAscending /> : <HiSortDescending />}
                        </IconToggle>
                    </Field>
                )}
            </DropdownGroup>
        </GroupInfo>
    );
};

const GroupInfo = styled.div`
  background: #f8fafc;
  border-radius: 10px;
  padding: 12px 16px;
  width: 100%;
  min-height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 16px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  max-width: 50%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownGroup = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
`;

const FieldLabel = styled.span`
  font: 700 12px/1 Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  color: #4b5563; /* gray-600 */
  text-transform: uppercase;
  letter-spacing: 0.04em;

`;

const SelectWrap = styled.div`
  position: relative;
  min-width: 220px;

  /* custom caret */
  &::after {
    content: "";
    position: absolute;
    right: 12px;
    top: 50%;
    width: 0; height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid #6b7280; /* gray-500 */
    transform: translateY(-50%);
    pointer-events: none;
  }
`;

const Select = styled.select`
  appearance: none;
  width: 100%;
  padding: 10px 36px 10px 12px;
  border: 1px solid #d1d5db;         /* gray-300 */
  border-radius: 8px;
  background: #fff;
  color: #111827;                     /* gray-900 */
  font: 500 14px/1.25 Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  transition: border-color .15s ease, box-shadow .15s ease;

  &:hover { border-color: #b189d5; }  /* subtle hover */
  &:focus {
    outline: none;
    border-color: #9333ea;            /* purple focus */
    box-shadow: 0 0 0 3px rgba(147, 51, 234, .2);
  }
`;

const IconToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 44px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;   /* gray-200 */
  background: #fff;
  color: #4b5563;
  cursor: pointer;
  transition: background .15s ease, border-color .15s ease;

  svg { display: block; width: 20px; height: 20px; }

  &:hover { border-color: #b189d5; }  /* subtle hover */
  &:focus {
    outline: none;
    border-color: #9333ea;            /* purple focus */
    box-shadow: 0 0 0 3px rgba(147, 51, 234, .2);
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 280px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 36px 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #111827;
  font: 500 14px/1.25 Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  transition: border-color .15s ease, box-shadow .15s ease;

  &:hover { border-color: #b189d5; }
  &:focus {
    outline: none;
    border-color: #9333ea;
    box-shadow: 0 0 0 3px rgba(147, 51, 234, .2);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: color .15s ease, background .15s ease;

  &:hover {
    color: #111827;
    background: #f3f4f6;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const SuggestionsList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
`;

const SuggestionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;
  transition: background .15s ease;

  &:hover {
    background: #f9fafb;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const StudentAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${props => props.bg || "#9333ea"};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
`;

const StudentInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const StudentName = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StudentEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default GroupHeader;
